import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Settings,
  Plus,
  Edit,
  Trash2,
  X,
  ArrowLeft,
  RefreshCw,
  Database,
  Hash,
  Tag,
  CheckCircle,
  Loader2
} from 'lucide-react'
import { useNotification } from '../../contexts/NotificationContext'

interface SystemPick {
  id: string
  name: string
  value: string
  category: 'status' | 'condition' | 'priority' | 'type' | 'package-type'
  active: boolean
  sort_order: number
  created_at: string
  updated_at: string
}

const API_BASE = import.meta.env.VITE_API_URL || ''

const SYSTEM_PICK_CATEGORIES: Array<{ value: SystemPick['category'], label: string }> = [
  { value: 'status', label: 'Status' },
  { value: 'condition', label: 'Condition' },
  { value: 'priority', label: 'Priority' },
  { value: 'type', label: 'Type' },
  { value: 'package-type', label: 'Package Type' }
]

export default function SystemPicksPage() {
  const navigate = useNavigate()
  const { success, error: showError } = useNotification()

  const [systemPicks, setSystemPicks] = useState<SystemPick[]>([])
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingPick, setEditingPick] = useState<SystemPick | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    value: '',
    category: 'status' as SystemPick['category'],
    active: true,
    sort_order: 0
  })
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<SystemPick['category'] | 'all'>('all')

  const filteredPicks = selectedCategory === 'all'
    ? systemPicks
    : systemPicks.filter(pick => pick.category === selectedCategory)

  const fetchSystemPicks = async () => {
    try {
      setRefreshing(true)
      const response = await fetch(`${API_BASE}/api/system-picks-pg`)
      const data = await response.json()

      if (data.success) {
        setSystemPicks(data.data)
      } else {
        showError('Failed to fetch system picks', data.error)
      }
    } catch (err) {
      console.error('Error fetching system picks:', err)
      showError('Error', 'Failed to fetch system picks')
    } finally {
      setRefreshing(false)
    }
  }

  useEffect(() => {
    fetchSystemPicks()
  }, [])

  const handleCreatePick = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim() || !formData.value.trim()) {
      showError('Validation Error', 'Name and value are required')
      return
    }

    setLoading(true)

    try {
      const response = await fetch(`${API_BASE}/api/system-picks-pg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      const data = await response.json()

      if (data.success) {
        success('Success', 'System pick created successfully')
        setSystemPicks(prev => [...prev, data.data])
        setFormData({ name: '', value: '', category: 'status', active: true, sort_order: 0 })
        setShowCreateDialog(false)
      } else {
        showError('Error', data.error)
      }
    } catch (err) {
      console.error('Error creating system pick:', err)
      showError('Error', 'Failed to create system pick')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdatePick = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!editingPick) return

    setLoading(true)

    try {
      const response = await fetch(`${API_BASE}/api/system-picks-pg`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: editingPick.id, ...formData })
      })

      const data = await response.json()

      if (data.success) {
        success('Success', 'System pick updated successfully')
        setSystemPicks(prev => prev.map(pick =>
          pick.id === editingPick.id ? data.data : pick
        ))
        setEditingPick(null)
        setFormData({ name: '', value: '', category: 'status', active: true, sort_order: 0 })
      } else {
        showError('Error', data.error)
      }
    } catch (err) {
      console.error('Error updating system pick:', err)
      showError('Error', 'Failed to update system pick')
    } finally {
      setLoading(false)
    }
  }

  const handleDeletePick = async (id: string) => {
    if (!confirm('Are you sure you want to delete this system pick?')) return

    try {
      const response = await fetch(`${API_BASE}/api/system-picks-pg?id=${id}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (data.success) {
        success('Success', 'System pick deleted successfully')
        setSystemPicks(prev => prev.filter(pick => pick.id !== id))
      } else {
        showError('Error', data.error)
      }
    } catch (err) {
      console.error('Error deleting system pick:', err)
      showError('Error', 'Failed to delete system pick')
    }
  }

  const startEdit = (pick: SystemPick) => {
    setEditingPick(pick)
    setFormData({
      name: pick.name,
      value: pick.value,
      category: pick.category,
      active: pick.active,
      sort_order: pick.sort_order
    })
  }

  const cancelEdit = () => {
    setEditingPick(null)
    setFormData({ name: '', value: '', category: 'status', active: true, sort_order: 0 })
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'status': return <Database className="h-5 w-5" />
      case 'condition': return <CheckCircle className="h-5 w-5" />
      case 'priority': return <Hash className="h-5 w-5" />
      case 'type': return <Tag className="h-5 w-5" />
      default: return <Settings className="h-5 w-5" />
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'status': return '#3b82f6' // blue
      case 'condition': return '#10b981' // green
      case 'priority': return '#f59e0b' // amber
      case 'type': return '#a855f7' // purple
      case 'package-type': return '#ec4899' // pink
      default: return '#6b7280' // gray
    }
  }

  if (refreshing && systemPicks.length === 0) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
        padding: '24px',
        fontFamily: 'Rajdhani, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 style={{ width: 48, height: 48, color: '#FF6B35', animation: 'spin 1s linear infinite' }} />
          <p style={{ color: '#8A95AA', marginTop: '16px' }}>Loading system picks...</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '48px',
              fontWeight: 700,
              color: '#FF6B35',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0,
              textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
            }}>
              SYSTEM PICKS MANAGEMENT
            </h1>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#8A95AA',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: '8px 0 0 0'
            }}>
              MANAGE DROPDOWN OPTIONS FOR STATUS, CONDITION, PRIORITY, AND TYPE
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                background: 'rgba(138, 149, 170, 0.1)',
                border: '2px solid rgba(138, 149, 170, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#8A95AA',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <ArrowLeft style={{ width: 16, height: 16 }} />
              BACK
            </button>
            <button
              onClick={fetchSystemPicks}
              disabled={refreshing}
              style={{
                background: 'rgba(0, 240, 255, 0.1)',
                border: '2px solid rgba(0, 240, 255, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#00F0FF',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: refreshing ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                opacity: refreshing ? 0.5 : 1
              }}
            >
              <RefreshCw style={{ width: 16, height: 16, animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
              REFRESH
            </button>
            <button
              onClick={() => setShowCreateDialog(true)}
              style={{
                background: 'rgba(185, 27, 255, 0.2)',
                border: '2px solid #B91BFF',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#B91BFF',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 0 20px rgba(185, 27, 255, 0.3)'
              }}
            >
              <Plus style={{ width: 16, height: 16 }} />
              CREATE PICK
            </button>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div style={{
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(8px)',
        border: '2px solid rgba(255, 20, 147, 0.3)',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '24px',
        boxShadow: '0 0 30px rgba(255, 20, 147, 0.15)'
      }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {[{ value: 'all' as const, label: 'All' }, ...SYSTEM_PICK_CATEGORIES].map(cat => {
            const isActive = selectedCategory === cat.value
            const color = cat.value === 'all' ? '#FF1493' : getCategoryColor(cat.value)

            return (
              <button
                key={cat.value}
                onClick={() => setSelectedCategory(cat.value as any)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '6px',
                  border: `2px solid ${isActive ? color : 'rgba(255, 20, 147, 0.3)'}`,
                  background: isActive ? `rgba(${cat.value === 'all' ? '255, 20, 147' : color === '#3b82f6' ? '59, 130, 246' : color === '#10b981' ? '16, 185, 129' : color === '#f59e0b' ? '245, 158, 11' : '168, 85, 247'}, 0.15)` : 'transparent',
                  color: isActive ? color : '#E0E6F0',
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  cursor: 'pointer',
                  boxShadow: isActive ? `0 0 20px ${color}40` : 'none'
                }}
              >
                {cat.label.toUpperCase()}
              </button>
            )
          })}
        </div>
      </div>

      {/* System Picks Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '16px'
      }}>
        {filteredPicks.map(pick => {
          const color = getCategoryColor(pick.category)

          return (
            <div
              key={pick.id}
              style={{
                background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.8), rgba(36, 41, 56, 0.8))',
                border: '2px solid rgba(255, 20, 147, 0.3)',
                borderRadius: '12px',
                padding: '20px',
                transition: 'all 0.3s'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '12px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '8px',
                  background: `rgba(${color === '#3b82f6' ? '59, 130, 246' : color === '#10b981' ? '16, 185, 129' : color === '#f59e0b' ? '245, 158, 11' : '168, 85, 247'}, 0.2)`,
                  border: `2px solid ${color}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {React.cloneElement(getCategoryIcon(pick.category), { style: { width: 24, height: 24, color } })}
                </div>

                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '20px',
                    fontWeight: 700,
                    color: '#FF1493',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    margin: 0
                  }}>
                    {pick.name}
                  </h3>
                  <p style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '12px',
                    color: '#8A95AA',
                    margin: '4px 0 0 0'
                  }}>
                    Value: <span style={{ fontFamily: 'monospace', color: '#B91BFF' }}>{pick.value}</span>
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '12px',
                paddingTop: '12px',
                borderTop: '1px solid rgba(255, 20, 147, 0.2)'
              }}>
                <div style={{
                  flex: 1,
                  background: `rgba(${color === '#3b82f6' ? '59, 130, 246' : color === '#10b981' ? '16, 185, 129' : color === '#f59e0b' ? '245, 158, 11' : '168, 85, 247'}, 0.1)`,
                  border: `1px solid ${color}`,
                  borderRadius: '6px',
                  padding: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '12px',
                    fontWeight: 700,
                    color,
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    {pick.category}
                  </div>
                  <div style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '10px',
                    color: '#8A95AA',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    Category
                  </div>
                </div>
                <div style={{
                  flex: 1,
                  background: pick.active ? 'rgba(107, 207, 127, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  border: pick.active ? '1px solid rgba(107, 207, 127, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '6px',
                  padding: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '12px',
                    fontWeight: 700,
                    color: pick.active ? '#6BCF7F' : '#EF4444',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    {pick.active ? 'Active' : 'Inactive'}
                  </div>
                  <div style={{
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '10px',
                    color: '#8A95AA',
                    textTransform: 'uppercase',
                    letterSpacing: '1px'
                  }}>
                    Status
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => startEdit(pick)}
                  style={{
                    flex: 1,
                    background: 'rgba(255, 217, 61, 0.1)',
                    border: '2px solid rgba(255, 217, 61, 0.4)',
                    borderRadius: '6px',
                    padding: '10px',
                    color: '#FFD93D',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Edit style={{ width: 14, height: 14 }} />
                  EDIT
                </button>
                <button
                  onClick={() => handleDeletePick(pick.id)}
                  style={{
                    flex: 1,
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '2px solid rgba(239, 68, 68, 0.4)',
                    borderRadius: '6px',
                    padding: '10px',
                    color: '#EF4444',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '12px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Trash2 style={{ width: 14, height: 14 }} />
                  DELETE
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Empty state */}
      {filteredPicks.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '48px 24px',
          background: 'rgba(0, 0, 0, 0.4)',
          borderRadius: '12px',
          border: '2px solid rgba(255, 20, 147, 0.3)'
        }}>
          <Settings style={{ width: 48, height: 48, color: '#FF1493', opacity: 0.5, marginBottom: '16px' }} />
          <p style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '16px',
            fontWeight: 700,
            color: '#E0E6F0',
            margin: '0 0 8px 0',
            letterSpacing: '1px'
          }}>
            NO SYSTEM PICKS FOUND
          </p>
          <p style={{
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '14px',
            color: '#8A95AA',
            margin: 0
          }}>
            {selectedCategory === 'all'
              ? 'Click "Create Pick" to get started'
              : `No ${selectedCategory} picks found`
            }
          </p>
        </div>
      )}

      {/* Create/Edit Dialog */}
      {(showCreateDialog || editingPick) && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 50
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(26, 31, 46, 0.95), rgba(36, 41, 56, 0.95))',
            backdropFilter: 'blur(12px)',
            border: '2px solid rgba(255, 20, 147, 0.4)',
            borderRadius: '12px',
            padding: '24px',
            width: '100%',
            maxWidth: '500px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '20px',
                fontWeight: 700,
                color: '#FF1493',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                margin: 0
              }}>
                {editingPick ? 'EDIT SYSTEM PICK' : 'CREATE SYSTEM PICK'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateDialog(false)
                  cancelEdit()
                }}
                style={{
                  background: 'rgba(138, 149, 170, 0.1)',
                  border: '2px solid rgba(138, 149, 170, 0.4)',
                  borderRadius: '6px',
                  padding: '8px',
                  color: '#8A95AA',
                  cursor: 'pointer'
                }}
              >
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>

            <form onSubmit={editingPick ? handleUpdatePick : handleCreatePick} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '11px',
                  color: '#8A95AA',
                  marginBottom: '6px',
                  display: 'block',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>NAME *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter name"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '11px',
                  color: '#8A95AA',
                  marginBottom: '6px',
                  display: 'block',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>VALUE *</label>
                <input
                  type="text"
                  value={formData.value}
                  onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                  placeholder="Enter value (lowercase, no spaces)"
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '11px',
                  color: '#8A95AA',
                  marginBottom: '6px',
                  display: 'block',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>CATEGORY *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as any }))}
                  required
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {SYSTEM_PICK_CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{
                  fontFamily: 'Rajdhani, sans-serif',
                  fontSize: '11px',
                  color: '#8A95AA',
                  marginBottom: '6px',
                  display: 'block',
                  letterSpacing: '1px',
                  textTransform: 'uppercase'
                }}>SORT ORDER</label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    background: 'rgba(0, 0, 0, 0.4)',
                    border: '2px solid rgba(255, 20, 147, 0.3)',
                    borderRadius: '6px',
                    color: '#FFFFFF',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    outline: 'none'
                  }}
                />
              </div>

              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                color: '#E0E6F0',
                fontWeight: 600
              }}>
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#FF1493'
                  }}
                />
                ACTIVE
              </label>

              <div style={{
                display: 'flex',
                gap: '12px',
                paddingTop: '16px',
                borderTop: '1px solid rgba(255, 20, 147, 0.2)'
              }}>
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    flex: 1,
                    background: 'rgba(107, 207, 127, 0.2)',
                    border: '2px solid #6BCF7F',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    color: '#6BCF7F',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.5 : 1
                  }}
                >
                  {loading ? 'SAVING...' : (editingPick ? 'UPDATE PICK' : 'CREATE PICK')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateDialog(false)
                    cancelEdit()
                  }}
                  style={{
                    background: 'rgba(138, 149, 170, 0.1)',
                    border: '2px solid rgba(138, 149, 170, 0.4)',
                    borderRadius: '6px',
                    padding: '12px 20px',
                    color: '#8A95AA',
                    fontFamily: 'Rajdhani, sans-serif',
                    fontSize: '14px',
                    fontWeight: 700,
                    letterSpacing: '1px',
                    textTransform: 'uppercase',
                    cursor: 'pointer'
                  }}
                >
                  CANCEL
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
